import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { clientApi } from '../../services/api';

interface FileWithPreview extends File {
  preview?: string;
}

export default function DepotPage() {
  const [clientName, setClientName] = useState('');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Calculate total size
    const currentSize = files.reduce((sum, f) => sum + f.size, 0);
    const newSize = acceptedFiles.reduce((sum, f) => sum + f.size, 0);

    if (currentSize + newSize > 1024 * 1024 * 1024) {
      toast.error('La taille totale des fichiers ne peut pas dépasser 1 Go');
      return;
    }

    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, [files]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxSize: 100 * 1024 * 1024, // 100MB per file
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'application/zip': ['.zip'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  });

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' Go';
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!clientName.trim()) {
      toast.error('Veuillez entrer votre nom');
      return;
    }

    if (!message.trim()) {
      toast.error('Veuillez entrer un message');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('clientName', clientName.trim());
      formData.append('message', message.trim());
      files.forEach((file) => formData.append('files', file));

      await clientApi.createDeposit(formData);

      setIsSuccess(true);
      toast.success('Votre dépôt a été enregistré avec succès');
    } catch (error) {
      console.error('Deposit error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center animate-fadeIn">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Dépôt enregistré</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Votre message a été transmis en toute sécurité. Il sera conservé pendant 7 jours maximum, puis automatiquement supprimé.
          </p>
          <button
            onClick={() => {
              setIsSuccess(false);
              setClientName('');
              setMessage('');
              setFiles([]);
            }}
            className="btn-primary"
          >
            Faire un nouveau dépôt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img
            src="/images/logo-black.png"
            alt="PowerITI"
            className="h-12 mx-auto mb-4 dark:hidden"
          />
          <img
            src="/images/logo-white.png"
            alt="PowerITI"
            className="h-12 mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Dépôt sécurisé</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Transmettez vos informations confidentielles en toute sécurité
          </p>
        </div>

        {/* Security badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Chiffrement de bout en bout
          </div>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Conservation : 7 jours max
          </div>
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <svg className="w-5 h-5 text-primary-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Suppression automatique
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="secure-card">
          {/* Client name */}
          <div className="mb-6">
            <label htmlFor="clientName" className="label">
              Votre nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="clientName"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              className="input"
              placeholder="Jean Dupont"
              required
            />
          </div>

          {/* Message */}
          <div className="mb-6">
            <label htmlFor="message" className="label">
              Message confidentiel <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="input min-h-[150px] resize-y"
              placeholder="Entrez votre message confidentiel ici..."
              required
            />
          </div>

          {/* File upload */}
          <div className="mb-6">
            <label className="label">Fichiers joints (optionnel)</label>
            <div
              {...getRootProps()}
              className={`dropzone ${isDragActive ? 'dropzone-active' : ''} ${isDragReject ? 'dropzone-reject' : ''}`}
            >
              <input {...getInputProps()} />
              <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-gray-600 dark:text-gray-400">
                {isDragActive
                  ? 'Déposez les fichiers ici...'
                  : 'Glissez-déposez des fichiers ici, ou cliquez pour sélectionner'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                PDF, DOC, XLS, TXT, CSV, ZIP, PNG, JPG (max 100 Mo par fichier, 1 Go au total)
              </p>
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
                <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                  Total : {formatFileSize(totalSize)}
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full py-3"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <span className="spinner mr-2" />
                Envoi en cours...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Envoyer en toute sécurité
              </span>
            )}
          </button>
        </form>

        {/* Watermark */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-8">
          PowerITI - Plateforme sécurisée d'échange d'informations
        </p>

        {/* Discrete admin link */}
        <div className="text-center mt-4">
          <Link
            to="/login"
            className="text-xs text-gray-300 hover:text-gray-500 dark:text-gray-600 dark:hover:text-gray-400 transition-colors"
          >
            Administration
          </Link>
        </div>
      </div>
    </div>
  );
}
